import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { voidReceipt } from '../../api/cheques';
import { useAuth } from '../../context/AuthContext';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const STATUS_COLORS = {
  IN_HAND:  { bg: '#fef9c3', color: '#854d0e', label: 'Cheque In Hand' },
  BANKED:   { bg: '#dcfce7', color: '#15803d', label: 'Banked' },
  RETURNED: { bg: '#fee2e2', color: '#b91c1c', label: 'Cheque Returned' },
  VOID:     { bg: '#fee2e2', color: '#dc2626', label: 'Void' },
  APPROVED: { bg: '#dbeafe', color: '#1d4ed8', label: 'Approved' },
};

export default function ReceiptVoucherPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';

  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voiding, setVoiding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (id) load(); }, [id]);

  async function load() {
    setLoading(true);
    const { data: v } = await supabase
      .from('receipt_vouchers')
      .select('*, currencies(code, name), clients(full_name, client_code, email, phone), received_into:received_into_account_id(account_code, account_name)')
      .eq('id', id)
      .single();
    setVoucher(v);
    setLoading(false);
  }

  async function handleVoid() {
    if (!window.confirm('Void this receipt voucher? This will reverse the journal entry.')) return;
    setVoiding(true); setError('');
    try {
      // Full undo, including a deposit already applied to an invoice (moved back onto AR).
      const { reopened, movedToAr } = await voidReceipt(voucher, { reason: 'Voided by user', username });
      if (movedToAr > 0 || reopened.length) {
        alert(`Voided.${movedToAr > 0 ? `\nLKR ${movedToAr.toLocaleString()} of this deposit had already been applied to invoices — it is back on the client's balance due.` : ''}${reopened.length ? `\nReopened: ${reopened.join(', ')}` : ''}`);
      }
      await load();
    } catch (e) { setError(e.message); }
    setVoiding(false);
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading…</div>;
  if (!voucher) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Voucher not found.</div>;

  const statusInfo = STATUS_COLORS[voucher.voucher_status] || { bg: '#f3f4f6', color: '#555', label: voucher.voucher_status };
  const currCode = voucher.currencies?.code || 'LKR';

  return (
    <>
      <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 24px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/vouchers/receipt')}>← Back</button>
        <div style={{ flex: 1 }} />
        {error && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</span>}
        {!['VOID', 'RETURNED'].includes(voucher.voucher_status) && (
          <button className="btn btn-danger" onClick={handleVoid} disabled={voiding}>{voiding ? 'Voiding…' : 'Void'}</button>
        )}
        <button className="btn btn-primary" onClick={() => window.print()}>Print / PDF</button>
      </div>

      <div style={{ maxWidth: 560, margin: '24px auto', padding: '28px 32px', background: 'white', boxShadow: '0 2px 20px rgba(0,0,0,0.08)', fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#111', borderRadius: 8 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <img src="/travelscape-logo-full.png" alt="Travelscape Lanka" style={{ height: 48, display: 'block' }} />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#16a34a' }}>RECEIPT VOUCHER</div>
            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>{voucher.voucher_number}</div>
            <div style={{ marginTop: 4, fontSize: 11, color: '#555' }}>Date: <strong>{fmtDate(voucher.voucher_date)}</strong></div>
            <div style={{ marginTop: 5 }}>
              <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: statusInfo.bg, color: statusInfo.color, border: `1px solid ${statusInfo.color}44` }}>
                {statusInfo.label}
              </span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '2px solid #16a34a', marginBottom: 18 }} />

        {/* Received From / Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Received From</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{voucher.received_from || voucher.clients?.full_name || '—'}</div>
            {voucher.clients?.client_code && <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>{voucher.clients.client_code}</div>}
            {voucher.clients?.phone && <div style={{ fontSize: 11, color: '#555' }}>{voucher.clients.phone}</div>}
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Payment Details</div>
            <table style={{ fontSize: 11, width: '100%' }}>
              <tbody>
                <tr><td style={{ color: '#888', paddingBottom: 3 }}>Mode:</td><td style={{ fontWeight: 600 }}>{voucher.payment_mode || '—'}</td></tr>
                {voucher.bank_reference && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Ref/Cheque:</td><td style={{ fontWeight: 600 }}>{voucher.bank_reference}</td></tr>}
                {voucher.received_into && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Deposited To:</td><td style={{ fontWeight: 600 }}>{voucher.received_into.account_code} · {voucher.received_into.account_name}</td></tr>}
                {voucher.exchange_rate && parseFloat(voucher.exchange_rate) !== 1 && <tr><td style={{ color: '#888', paddingBottom: 3 }}>Rate:</td><td style={{ fontWeight: 600 }}>{voucher.exchange_rate} LKR</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Amount */}
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount Received</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#15803d', marginTop: 3 }}>{currCode} {fmt(voucher.amount)}</div>
            {voucher.base_amount && parseFloat(voucher.base_amount) !== parseFloat(voucher.amount) && (
              <div style={{ fontSize: 11, color: '#15803d', marginTop: 1 }}>≈ LKR {fmt(voucher.base_amount)}</div>
            )}
          </div>
        </div>

        {voucher.description && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Narration</div>
            <div style={{ fontSize: 12, color: '#333' }}>{voucher.description}</div>
          </div>
        )}

        <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginTop: 20, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#aaa' }}>
          <div>Travelscape Lanka Pvt Ltd</div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ borderTop: '1px solid #ccc', marginTop: 24, paddingTop: 4, width: 140 }}>Authorised Signature</div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A5; margin: 10mm; }
          body { margin: 0; }
          div[style*="maxWidth: 560"] { box-shadow: none !important; margin: 0 !important; border-radius: 0 !important; max-width: 100% !important; padding: 0 !important; }
        }
      `}</style>
    </>
  );
}
